import React, { CSSProperties } from "react";
import { range, seeded } from "./sceneUtils";

/*
 * Verktygslåda för de illustrerade scenerna.
 *
 * Scenerna byggs som ett landskap i lager (långt bort -> nära), där varje
 * lager är en SVG i samma koordinatsystem (W x H) som skalas med
 * `xMidYMax slice`. Lager längre bort får färger som blandats mot himlen
 * (atmosfäriskt perspektiv), vilket ger djup utan några filter.
 */

export const W = 1600;
export const H = 600;

/* ---------- Färg ---------- */

const toRgb = (c: string): [number, number, number] => {
  const n = parseInt(c.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/** Blanda två hexfärger. t = 0 ger a, t = 1 ger b. */
export const mix = (a: string, b: string, t: number): string => {
  const A = toRgb(a);
  const B = toRgb(b);
  return (
    "#" +
    A.map((v, i) => Math.round(v + (B[i] - v) * t).toString(16).padStart(2, "0")).join("")
  );
};

/* ---------- Brus ---------- */

/** Mjukt 1D-värdebrus i intervallet 0..1. */
const noise1D = (seed: number) => {
  const rnd = seeded(seed);
  const lattice = range(256).map(() => rnd());
  return (x: number) => {
    const i = Math.floor(x);
    const f = x - i;
    const u = f * f * (3 - 2 * f);
    const a = lattice[i & 255];
    const b = lattice[(i + 1) & 255];
    return a + (b - a) * u;
  };
};

/** Fraktalt brus (flera oktaver) i intervallet 0..1. */
export const fbm = (seed: number, octaves = 4) => {
  const layers = range(octaves).map(o => noise1D(seed + o * 101));
  return (x: number) => {
    let sum = 0;
    let amp = 1;
    let freq = 1;
    let norm = 0;
    for (let o = 0; o < octaves; o++) {
      sum += layers[o](x * freq) * amp;
      norm += amp;
      amp *= 0.5;
      freq *= 2;
    }
    return sum / norm;
  };
};

/* ---------- Horisontlinjer ---------- */

export interface Ridge {
  /** Fylld siluett ner till botten av lagret. */
  d: string;
  /** Linjens höjd vid x. */
  y: (x: number) => number;
}

export interface RidgeOptions {
  seed: number;
  /** Lägsta punkten (dalar) i lagrets koordinater. */
  base: number;
  /** Hur högt topparna når ovanför base. */
  amp: number;
  /** Antal "kullar" över bredden. */
  scale?: number;
  octaves?: number;
  /** 0 = mjuka kullar, 1 = spetsiga bergstoppar. */
  sharp?: number;
  /** Extra höjdprofil, t.ex. för att lyfta kanterna. */
  shape?: (x: number) => number;
}

export const ridge = ({
  seed,
  base,
  amp,
  scale = 3,
  octaves = 4,
  sharp = 0,
  shape,
}: RidgeOptions): Ridge => {
  const n = fbm(seed, octaves);
  const y = (x: number) => {
    let v = n((x / W) * scale + 7.3);
    if (sharp) {
      const r = 1 - Math.abs(v * 2 - 1);
      v = v * (1 - sharp) + r * r * sharp;
    }
    return base - v * amp - (shape ? shape(x) : 0);
  };
  const pts: string[] = [];
  for (let x = -40; x <= W + 40; x += 8) {
    pts.push(`${x} ${y(x).toFixed(1)}`);
  }
  return { d: `M-40 ${H + 10} L${pts.join(" L")} L${W + 40} ${H + 10} Z`, y };
};

/* ---------- Granar ---------- */

/**
 * En gran som en taggig siluett. (x, y) är stammens fot, h är höjden.
 * `body` är hela granen, `snow` snötäcket som ligger på grenarnas ovansidor.
 */
export const pineParts = (x: number, y: number, h: number, rnd: () => number): { body: string; snow: string } => {
  const tiers = 6 + Math.floor(rnd() * 3) + Math.floor(h / 80);
  const wMax = h * (0.27 + rnd() * 0.07);
  const top = y - h;
  const trunkH = h * 0.08;
  const crownH = h - trunkH;
  const left: [number, number][] = [];
  const right: [number, number][] = [];
  const lean = (rnd() - 0.5) * h * 0.04;
  for (let i = 0; i < tiers; i++) {
    const t = (i + 1) / tiers;
    const ty = top + crownH * t;
    const w = wMax * (0.18 + 0.82 * t) * (0.88 + rnd() * 0.24);
    const droop = h * (0.02 + rnd() * 0.025);
    const cx = x + lean * (1 - t);
    left.push([cx - w, ty + droop]);
    right.push([cx + w * (0.9 + rnd() * 0.2), ty + droop]);
    if (i < tiers - 1) {
      const notchY = ty - crownH * 0.02;
      left.push([cx - w * 0.42, notchY]);
      right.push([cx + w * 0.42, notchY]);
    }
  }
  const tw = Math.max(1.2, h * 0.03);
  const trunkTop = top + crownH;
  const f = ([px, py]: [number, number]) => `${px.toFixed(1)} ${py.toFixed(1)}`;
  const tip: [number, number] = [x + lean, top];
  const body =
    `M${f(tip)} L` +
    left.map(f).join(" L") +
    ` L${f([x - tw, trunkTop])} L${f([x - tw, y])} L${f([x + tw, y])} L${f([x + tw, trunkTop])} L` +
    [...right].reverse().map(f).join(" L") +
    " Z";
  // Snö: på varje grens ovansida (från föregående inskärning ut till spetsen)
  const t = Math.min(h * 0.018, 4.5);
  const clumps: string[] = [];
  const side = (pts: [number, number][], dir: number) => {
    let prev = tip;
    for (let i = 0; i < pts.length; i += 2) {
      const outer = pts[i];
      const ax = prev[0] + (outer[0] - prev[0]) * 0.3;
      const ay = prev[1] + (outer[1] - prev[1]) * 0.3;
      const mx = prev[0] + (outer[0] - prev[0]) * 0.68;
      const my = prev[1] + (outer[1] - prev[1]) * 0.68;
      clumps.push(
        `M${f([ax, ay - t * 0.3])} L${f([mx, my - t * 0.5])} L${f([outer[0] - dir * t * 0.3, outer[1] - t * 0.4])} ` +
          `L${f([outer[0] + dir * t * 1.2, outer[1] + t * 0.6])} L${f([mx + dir * t * 0.8, my + t * 1.1])} L${f([ax + dir * t * 0.4, ay + t * 0.9])} Z`
      );
      prev = pts[i + 1] ?? outer;
    }
  };
  side(left, 1);
  side(right, -1);
  clumps.push(`M${f(tip)} L${f([tip[0] - h * 0.035, top + h * 0.09])} L${f([tip[0], top + h * 0.075])} L${f([tip[0] + h * 0.035, top + h * 0.09])} Z`);
  return { body, snow: clumps.join(" ") };
};

export const pine = (x: number, y: number, h: number, rnd: () => number): string => pineParts(x, y, h, rnd).body;

export interface ForestOptions {
  seed: number;
  from: number;
  to: number;
  /** Genomsnittligt avstånd mellan granarna. */
  gap: number;
  minH: number;
  maxH: number;
  /** Hur långt ner i marken stammen står. */
  sink?: number;
  /** Hoppa över granar i intervallet (t.ex. för en by). */
  skip?: [number, number][];
}

export interface Tree {
  x: number;
  y: number;
  h: number;
}

/** Placerar granar längs en horisontlinje. */
export const forestTrees = (r: Ridge, o: ForestOptions): Tree[] => {
  const rnd = seeded(o.seed);
  const trees: Tree[] = [];
  let x = o.from;
  while (x < o.to) {
    const at = x;
    const skip = o.skip?.some(([a, b]) => at > a && at < b);
    if (!skip) {
      const h = o.minH + rnd() * (o.maxH - o.minH);
      trees.push({ x, y: r.y(x) + (o.sink ?? 4), h });
    }
    x += o.gap * (0.45 + rnd() * 0.9);
  }
  // Minsta granarna först så att de större hamnar framför.
  return trees.sort((a, b) => a.h - b.h);
};

export const forestPath = (trees: Tree[], seed: number, dy = 0): string => {
  const rnd = seeded(seed);
  return trees.map(t => pine(t.x, t.y + dy, t.h, rnd)).join(" ");
};

/** Snötäcket för samma granar som forestPath(trees, seed). */
export const forestSnowPath = (trees: Tree[], seed: number): string => {
  const rnd = seeded(seed);
  return trees.map(t => pineParts(t.x, t.y, t.h, rnd).snow).join(" ");
};

/* ---------- Kala träd ---------- */

/**
 * Ett knotigt, kalt träd som en samling avsmalnande grenar.
 * Används för Halloween. Returnerar en path för fyllning.
 */
export const bareTree = (
  x: number,
  y: number,
  len: number,
  width: number,
  seed: number,
  depth = 7,
  angle = -Math.PI / 2
): string => {
  const rnd = seeded(seed);
  const out: string[] = [];
  const branch = (bx: number, by: number, l: number, a: number, w: number, d: number) => {
    const x2 = bx + Math.cos(a) * l;
    const y2 = by + Math.sin(a) * l;
    const w2 = w * (0.62 + rnd() * 0.1);
    const px = -Math.sin(a);
    const py = Math.cos(a);
    const bend = (rnd() - 0.5) * l * 0.35;
    const mx = (bx + x2) / 2 + px * bend;
    const my = (by + y2) / 2 + py * bend;
    const wm = (w + w2) / 4;
    out.push(
      `M${(bx + (px * w) / 2).toFixed(1)} ${(by + (py * w) / 2).toFixed(1)}` +
        ` Q${(mx + px * wm).toFixed(1)} ${(my + py * wm).toFixed(1)} ${(x2 + (px * w2) / 2).toFixed(1)} ${(y2 + (py * w2) / 2).toFixed(1)}` +
        ` L${(x2 - (px * w2) / 2).toFixed(1)} ${(y2 - (py * w2) / 2).toFixed(1)}` +
        ` Q${(mx - px * wm).toFixed(1)} ${(my - py * wm).toFixed(1)} ${(bx - (px * w) / 2).toFixed(1)} ${(by - (py * w) / 2).toFixed(1)} Z`
    );
    // Rund led så att grenarna går ihop utan hack
    const r = w2 / 2;
    out.push(`M${(x2 - r).toFixed(1)} ${y2.toFixed(1)} a${r.toFixed(1)} ${r.toFixed(1)} 0 1 0 ${(2 * r).toFixed(1)} 0 a${r.toFixed(1)} ${r.toFixed(1)} 0 1 0 ${(-2 * r).toFixed(1)} 0`);
    if (d <= 0 || w2 < 0.7) return;
    const n = rnd() < 0.35 ? 3 : 2;
    for (let i = 0; i < n; i++) {
      const spread = 0.38 + rnd() * 0.4;
      const na = a + (i - (n - 1) / 2) * spread + (rnd() - 0.5) * 0.35;
      branch(x2, y2, l * (0.66 + rnd() * 0.16), na, w2, d - 1);
    }
  };
  branch(x, y, len, angle, width, depth);
  return out.join(" ");
};

/* ---------- Former ---------- */

/** Snödriva med mjukt bucklig överkant, från x1 till x2 med överkanten på y. */
export const snowCap = (x1: number, x2: number, y: number, h: number, seed: number) => {
  const rnd = seeded(seed);
  const n = Math.max(2, Math.round((x2 - x1) / 9));
  const step = (x2 - x1) / n;
  let d = `M${x1 - 0.6} ${y + h} Q${x1 - 1} ${y} ${x1 + step * 0.3} ${y}`;
  for (let i = 0; i < n; i++) {
    const bump = 0.4 + rnd() * h * 0.5;
    d += ` Q${(x1 + step * (i + 0.5)).toFixed(1)} ${(y - bump).toFixed(1)} ${(x1 + step * (i + 1)).toFixed(1)} ${y}`;
  }
  return `${d} Q${x2 + 1} ${y} ${x2 + 0.6} ${y + h} Z`;
};


export const circlePath = (cx: number, cy: number, r: number) =>
  `M${cx - r} ${cy} a${r} ${r} 0 1 0 ${2 * r} 0 a${r} ${r} 0 1 0 ${-2 * r} 0 Z`;

/* ---------- Lager och himmel ---------- */

interface LayerProps {
  children: React.ReactNode;
  /** Fördröjning för entrén (lagren reser sig ur marken). */
  delay?: number;
  className?: string;
  style?: CSSProperties;
}

/** Ett landskapslager längst ner, i scenens gemensamma koordinatsystem. */
export const Layer: React.FC<LayerProps> = ({ children, delay = 0, className = "", style }) => (
  <div className={`hol-layer ${className}`.trim()} style={{ animationDelay: `${delay}ms`, ...style }}>
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMax slice" xmlns="http://www.w3.org/2000/svg">
      {children}
    </svg>
  </div>
);

/** Stjärnhimmel som täcker skärmen. Tre grupper blinkar i otakt. */
export const Stars: React.FC<{ seed: number; count: number; color?: string; maxY?: number }> = ({
  seed,
  count,
  color = "#ffffff",
  maxY = 560,
}) => {
  const rnd = seeded(seed);
  const stars = range(count).map(() => ({
    x: rnd() * 1600,
    y: Math.pow(rnd(), 1.4) * maxY,
    r: 0.4 + Math.pow(rnd(), 3) * 1.6,
    o: 0.35 + rnd() * 0.65,
    g: Math.floor(rnd() * 3),
  }));
  const bright = stars.filter(s => s.r > 1.45);
  return (
    <svg className="hol-stars" viewBox="0 0 1600 900" preserveAspectRatio="xMidYMin slice" xmlns="http://www.w3.org/2000/svg">
      {range(3).map(g => (
        <g key={g} className={`hol-star-group hol-star-group-${g}`} fill={color}>
          {stars
            .filter(s => s.g === g)
            .map((s, i) => (
              <circle key={i} cx={s.x.toFixed(1)} cy={s.y.toFixed(1)} r={s.r.toFixed(2)} opacity={s.o.toFixed(2)} />
            ))}
        </g>
      ))}
      {/* Några klara stjärnor med ljuskors */}
      <g stroke={color} strokeLinecap="round">
        {bright.map((s, i) => (
          <g key={i} opacity={0.55} className={`hol-star-group hol-star-group-${i % 3}`}>
            <line x1={s.x - s.r * 5} y1={s.y} x2={s.x + s.r * 5} y2={s.y} strokeWidth={0.5} />
            <line x1={s.x} y1={s.y - s.r * 5} x2={s.x} y2={s.y + s.r * 5} strokeWidth={0.5} />
          </g>
        ))}
      </g>
    </svg>
  );
};

/** Filmkorn över hela scenen – tar bort den platta "vektorkänslan". */
export const Grain: React.FC<{ opacity?: number }> = ({ opacity = 0.07 }) => (
  <div className="hol-grain" style={{ opacity }} />
);

/** Dimbank som driver långsamt i sidled. */
export const Mist: React.FC<{
  bottom: string;
  height: string;
  color: string;
  opacity?: number;
  duration?: number;
  delay?: number;
  reverse?: boolean;
}> = ({ bottom, height, color, opacity = 1, duration = 60, delay = 0, reverse }) => (
  <div
    className="hol-mist"
    style={{
      bottom,
      height,
      opacity,
      animationDuration: `${duration}s`,
      animationDelay: `${delay}s`,
      animationDirection: reverse ? "alternate-reverse" : "alternate",
      background: [
        `radial-gradient(ellipse 22% 50% at 12% 60%, ${color}, transparent 70%)`,
        `radial-gradient(ellipse 28% 45% at 38% 55%, ${color}, transparent 70%)`,
        `radial-gradient(ellipse 24% 55% at 63% 62%, ${color}, transparent 70%)`,
        `radial-gradient(ellipse 26% 48% at 88% 58%, ${color}, transparent 70%)`,
      ].join(","),
    }}
  />
);

/* ---------- Bergstoppar ---------- */

export interface Peak {
  x: number;
  /** Toppens höjd (y). */
  top: number;
  /** Halva bredden vid foten. */
  w: number;
}

export interface MountainShape {
  body: string;
  snow: string;
  shadow: string;
}

/**
 * Tydliga alptoppar med snötäcke och en skuggsida. Ljuset kommer från
 * höger, så vänstra sluttningen ligger i skugga.
 */
export const mountains = (peaks: Peak[], base: number, seed: number, snowLine = 0.38): MountainShape[] => {
  const rnd = seeded(seed);
  const jagged = (x0: number, y0: number, x1: number, y1: number, n: number, amt: number) => {
    const pts: [number, number][] = [];
    for (let i = 1; i < n; i++) {
      const t = i / n;
      pts.push([x0 + (x1 - x0) * t + (rnd() - 0.5) * amt, y0 + (y1 - y0) * t + (rnd() - 0.5) * amt * 0.6]);
    }
    return pts;
  };
  const fmt = (pts: [number, number][]) => pts.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(" L");
  return [...peaks]
    .sort((a, b) => a.top - b.top)
    .map(({ x, top, w }) => {
      const hgt = base - top;
      const left = jagged(x - w, base, x, top, 9, hgt * 0.06);
      const right = jagged(x, top, x + w, base, 9, hgt * 0.06);
      const body = `M${x - w} ${base + 4} L${fmt(left)} L${x} ${top} L${fmt(right)} L${x + w} ${base + 4} Z`;
      const spineEnd = x - w * (0.05 + rnd() * 0.15);
      const spine = jagged(x, top, spineEnd, base, 7, hgt * 0.09);
      const shadow = `M${x - w} ${base + 4} L${fmt(left)} L${x} ${top} L${fmt(spine)} L${spineEnd} ${base + 4} Z`;
      const sy = top + hgt * snowLine;
      const sl = x - w * snowLine;
      const sr = x + w * snowLine;
      const drips: [number, number][] = [];
      const n = 7;
      for (let i = 1; i < n; i++) {
        const t = i / n;
        drips.push([sr + (sl - sr) * t, sy + (i % 2 ? hgt * (0.05 + rnd() * 0.1) : -hgt * rnd() * 0.05)]);
      }
      const leftTop = left.filter(([, py]) => py < sy);
      const rightTop = right.filter(([, py]) => py < sy);
      const snow = `M${sl} ${sy} L${fmt(leftTop)} L${x} ${top} L${fmt(rightTop)} L${sr} ${sy} L${fmt(drips)} Z`;
      return { body, snow, shadow };
    });
};
